import { createSelector, createSlice } from "@reduxjs/toolkit";
import cachedWeather from "../weather/bothyForecasts.json";

const initialState = {
  forecastsByBothyId: cachedWeather ?? {}
};

const weatherSlice = createSlice({
  name: "weather",
  initialState,
  reducers: {}
});

export default weatherSlice.reducer;

const selectWeatherState = state => state.weather;

export const selectWeatherSummaryForBothy = createSelector(
  [
    selectWeatherState,
    (_, bothyId) => bothyId
  ],
  (weatherState, bothyId) => {
    if (!bothyId) {
      return { data: null, snow: null, loading: false, error: null, meta: null, snowMeta: null };
    }

    const entry = weatherState.forecastsByBothyId[bothyId];
    return {
      data: entry?.data ?? null,
      snow: entry?.snow ?? null,
      loading: false,
      error: entry ? null : "No cached weather available",
      meta: entry
        ? {
            lat: entry.lat,
            lon: entry.lon,
            fetchedAt: entry.fetchedAt ?? null
          }
        : null,
      snowMeta: entry?.snow
        ? {
            lat: entry.lat,
            lon: entry.lon,
            fetchedAt: entry.snowFetchedAt ?? entry.fetchedAt ?? null
          }
        : null
    };
  }
);

export const selectWeatherDayOptions = createSelector(
  [selectWeatherState],
  weatherState => {
    const forecasts = weatherState?.forecastsByBothyId ?? {};
    const times = Object.values(forecasts)
      .map(entry => entry?.data?.daily?.time)
      .find(values => Array.isArray(values) && values.length > 0);

    if (!times) {
      return [];
    }

    return times.map((isoDate, index) => ({
      index,
      isoDate
    }));
  }
);

export const selectWeatherUnits = createSelector(
  [selectWeatherState],
  weatherState => {
    const forecasts = weatherState?.forecastsByBothyId ?? {};
    const sample = Object.values(forecasts).find(
      entry =>
        entry?.data?.daily_units ||
        entry?.data?.hourly_units ||
        entry?.snow?.daily_units ||
        entry?.snow?.hourly_units
    );
    return {
      daily: {
        ...(sample?.data?.daily_units ?? {}),
        ...(sample?.snow?.daily_units ?? {})
      },
      hourly: {
        ...(sample?.data?.hourly_units ?? {}),
        ...(sample?.snow?.hourly_units ?? {})
      }
    };
  }
);

const updateRange = (range, value) => {
  if (!Number.isFinite(value)) {
    return range;
  }
  if (!range) {
    return { min: value, max: value };
  }
  return {
    min: Math.min(range.min, value),
    max: Math.max(range.max, value)
  };
};

const convertDepthToMm = (value, unit) => {
  if (!Number.isFinite(value)) {
    return value;
  }
  const lower = (unit || "").toLowerCase();
  if (lower === "m") return value * 1000;
  if (lower === "cm") return value * 10;
  return value;
};

export const selectWeatherRanges = createSelector(
  [selectWeatherState],
  weatherState => {
    const forecasts = weatherState?.forecastsByBothyId ?? {};

    const ranges = {
      precipitation: { range: null, unit: null },
      rain: { range: null, unit: null },
      snowfall: { range: null, unit: null },
      snowDepth: { range: null, unit: "mm" },
      wind: { range: null, unit: null }
    };

    Object.values(forecasts).forEach(entry => {
      const daily = entry?.data?.daily ?? {};
      const dailyUnits = entry?.data?.daily_units ?? {};
      const snowDaily = entry?.snow?.daily ?? {};
      const snowUnits = entry?.snow?.daily_units ?? {};

      (daily.precipitation_sum ?? []).forEach(value => {
        ranges.precipitation.range = updateRange(ranges.precipitation.range, Number(value));
      });
      ranges.precipitation.unit = ranges.precipitation.unit ?? dailyUnits.precipitation_sum;

      (daily.rain_sum ?? []).forEach(value => {
        ranges.rain.range = updateRange(ranges.rain.range, Number(value));
      });
      ranges.rain.unit = ranges.rain.unit ?? dailyUnits.rain_sum;

      const snowfallValues = (snowDaily.snowfall_sum ?? daily.snowfall_sum) ?? [];
      snowfallValues.forEach(value => {
        ranges.snowfall.range = updateRange(ranges.snowfall.range, Number(value));
      });
      ranges.snowfall.unit = ranges.snowfall.unit ?? (snowUnits.snowfall_sum || dailyUnits.snowfall_sum);

      const snowDepthValues = (snowDaily.snow_depth_max ?? daily.snow_depth_max) ?? [];
      const depthUnit = snowUnits.snow_depth_max || dailyUnits.snow_depth_max;
      snowDepthValues.forEach(value => {
        const mmValue = convertDepthToMm(Number(value), depthUnit);
        ranges.snowDepth.range = updateRange(ranges.snowDepth.range, mmValue);
      });

      (daily.wind_speed_10m_max ?? []).forEach(value => {
        ranges.wind.range = updateRange(ranges.wind.range, Number(value));
      });
      ranges.wind.unit = ranges.wind.unit ?? dailyUnits.wind_speed_10m_max;
    });

    return ranges;
  }
);
